import { getDataset } from "@/gen_src/proto/chalk/server/v1/datasets-DatasetMetadataService_connectquery";
import { DatasetMeta } from "@/gen_src/proto/chalk/server/v1/datasets_pb";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useQuery } from "@connectrpc/connect-query";
import { useMemo } from "react";

const NO_BREADCRUMB_LABELS: Readonly<Record<string, string>> = {};

/**
 * Replaces the opaque dataset id in detail-page breadcrumbs with the dataset name.
 * The shared GetDataset query cache keeps this in sync with both dataset and revision pages.
 */
export const useDatasetBreadcrumbLabels = (): Readonly<Record<string, string>> => {
  const { datasetId } = useRouteInfo();
  const datasetQuery = useQuery(getDataset, { id: datasetId }, { enabled: datasetId != null });
  const dataset = datasetQuery.data?.dataset;
  const label = dataset == null ? "" : dataset.datasetName || dataset.id;

  return useMemo(
    () => (datasetId == null ? NO_BREADCRUMB_LABELS : { datasetId: label }),
    [datasetId, label],
  );
};

/**
 * Returns the most recent revision with data (i.e., has an offlineQueryId).
 * Since gRPC only returns mostRecentRevision, this is a simplified version.
 */
export const useLatestDatasetRevision = (dataset: DatasetMeta) => {
  return useMemo(() => {
    const revision = dataset.mostRecentRevision;
    if (revision && revision.offlineQueryId) {
      return revision;
    }
    return undefined;
  }, [dataset.mostRecentRevision]);
};
